import { PublishedFormId } from '@furnish-hx/constants/FormId';
import FormTypeId from '@core/constants/FormTypeId';
import { FencingOptionId } from '@core/constants/Option';
import { MaterialId } from '@core/constants/Material';
import { FencingTypeId } from '@core/constants/Shape';
import { FieldDimensionId } from '@core/constants/FieldDimension';
import fieldPortfolioData from './common/fieldsPortfolioData';
import {
    IFormFieldKey,
    IForm,
    IFormInputType,
    IDimensionUnit,
} from '../../interfaces/Form';
import additionalDataFactory from './common/fieldsAdditionalData';
import fieldsContact from './common/fieldsContact';
import {
    allCompositeWoods,
    allConcretes,
    allCorian,
    allDekton,
    allMetals,
    allPlastics,
    allSolidWoods,
    getMaterialsWithExceptions,
} from './common/materialGroups';

const FencingForm: IForm = {
    formId       : PublishedFormId.Fencing,
    type         : FormTypeId.Fencing,
    thumbnailURL : '/resized-images/forms/fencing.jpg',
    imagesURLs   : [
        '/resized-images/forms/description/fencing/fencing-pvc.jpg',
        '/resized-images/forms/description/fencing/fencing-wood.jpg',
    ],
    fieldsOrder: [
        IFormFieldKey.PortfolioData,
        IFormFieldKey.Comment,
        IFormFieldKey.FencingDestination,
        IFormFieldKey.Material,
        IFormFieldKey.Type,
        IFormFieldKey.Dimension,
        IFormFieldKey.FenceGateDimension,
        IFormFieldKey.FencingPose,
        IFormFieldKey.AdditionalData,
        IFormFieldKey.Contact,
    ],
    fields: {
        portfolioData : fieldPortfolioData,
        comment       : {
            comment: {
                value    : undefined,
                required : false,
            },
        },
        fencingDestination: {
            value   : undefined,
            options : [
                FencingOptionId.FencingUseProtect,
                FencingOptionId.FencingUseDelimit,
                FencingOptionId.FencingUseBring,
                FencingOptionId.FencingUseOther,
            ],
            required: true,
        },
        material: {
            material: {
                value   : undefined,
                options : getMaterialsWithExceptions(
                    [
                        ...allSolidWoods,
                        ...allCompositeWoods,
                        ...allMetals,
                        ...allConcretes,
                        ...allPlastics,
                    ],
                    [
                        MaterialId.Beech,
                        MaterialId.WagonfloorDark,
                        MaterialId.WagonfloorLight,
                        MaterialId.OakOld,
                        MaterialId.Walnut,
                        MaterialId.Ash,
                        MaterialId.AshOlive,
                        MaterialId.Acacia,
                        MaterialId.Mahogany,
                        MaterialId.Elm,
                        MaterialId.Alderwood,
                        MaterialId.Birch,
                        MaterialId.Maple,
                        MaterialId.Wenge,
                        MaterialId.Cherrywood,
                        MaterialId.Ebony,
                        MaterialId.Rubber,
                        MaterialId.DriftWood,
                        MaterialId.Poplar,
                        MaterialId.Redcedar,
                        MaterialId.Iroko,
                        MaterialId.BlackLocust,
                        MaterialId.Steel,
                        MaterialId.Rust,
                        MaterialId.Brass,
                        MaterialId.Iron,
                        MaterialId.ThermoLaqueredBlack,
                        MaterialId.ThermoLaqueredWhite,
                        MaterialId.SheetMetal,
                        MaterialId.StainlessSteel,
                        MaterialId.Zinc,
                        MaterialId.PlexiglassFluorescent,
                        MaterialId.PlexiglassFrosted,
                        MaterialId.PlexiglassMirror,
                        MaterialId.PlexiglassTransparent,
                        MaterialId.PlexiglassWhite,
                        MaterialId.PolycarbonateOpaque,
                        MaterialId.PolycarbonateTeinted,
                        MaterialId.PolycarbonateTransparent,
                        MaterialId.RecycledPlastic,
                        ...allDekton,
                        ...allCorian,
                    ],
                ),
                required: true,
            },
        },
        type: {
            value   : undefined,
            options : [
                FencingTypeId.FencingOpenWork,
                FencingTypeId.FencingSolid,
                FencingTypeId.FencingOther,
            ],
            required: true,
        },
        dimension: {
            [FieldDimensionId.Height]: {
                value        : undefined,
                type         : IFormInputType.Range,
                unit         : IDimensionUnit.Centimeters,
                step         : 100,
                range        : { min: 500, max: 3200 },
                required     : true,
                defaultValue : 1500,
            },
            [FieldDimensionId.Length]: {
                value        : undefined,
                type         : IFormInputType.Range,
                unit         : IDimensionUnit.Meters,
                step         : 1000,
                range        : { min: 10000, max: 5000000 },
                required     : true,
                defaultValue : 100000,
            },
        },
        fenceGateDimension: {
            [FieldDimensionId.Height]: {
                value        : undefined,
                type         : IFormInputType.Range,
                unit         : IDimensionUnit.Centimeters,
                step         : 100,
                range        : { min: 500, max: 3200 },
                required     : false,
                defaultValue : 1000,
            },
            [FieldDimensionId.Length]: {
                value        : undefined,
                type         : IFormInputType.Range,
                unit         : IDimensionUnit.Centimeters,
                step         : 100,
                range        : { min: 500, max: 2000 },
                required     : false,
                defaultValue : 1500,
            },
        },
        fencingPose: {
            value   : undefined,
            options : [
                FencingOptionId.FencingPoseWall,
                FencingOptionId.FencingPoseGround,
                FencingOptionId.FencingPoseOther,
            ],
            required: false,
        },
        additionalData: additionalDataFactory({
            budgetMin          : 1500,
            budgetMax          : 10000,
            budgetDefaultValue : 3000,
            hasQuantity        : false,
        }),
        contact: fieldsContact,
    },
};

export default FencingForm;
